import * as React from "react";
import * as LocalForage from "localforage";
import * as React44ms from "../../../Util/form-utils/react-44ms";

export interface DataMutableSourceShippingClientStorageDataConsumerInterface {
  hasInitialized: boolean;

  lastSelectedSavedAddressId?: any;
  lastSelected_isBillingAndShippingTheSame?: any;
  lastSelected_authoriseDeliveryToBeLeftWithoutSignature?: any;
}
const defaultValue =
  {} as unknown as DataMutableSourceShippingClientStorageDataConsumerInterface;
const DataMutableSourceShippingClientStorageDataContext =
  React.createContext(defaultValue);
const DataMutableSourceShippingClientStorageDataContext_44 =
  React44ms.React.createContext(
    defaultValue,
    DataMutableSourceShippingClientStorageDataContext
  );

export interface DataMutableSourceShippingClientStorageData_ControllerInterface {
  handleLastSelectedSavedAddressIdChange: (
    lastSelectedSavedAddressId: any
  ) => Promise<void>;
  handleLastSelected_isBillingAndShippingTheSameChange: (
    lastSelected_isBillingAndShippingTheSame: any
  ) => Promise<void>;
  handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange: (
    lastSelected_authoriseDeliveryToBeLeftWithoutSignature: any
  ) => Promise<void>;
}
const controllerDefaultValue =
  {} as unknown as DataMutableSourceShippingClientStorageData_ControllerInterface;
const DataMutableSourceShippingClientStorageData_ControllerContext =
  React.createContext(controllerDefaultValue);

enum DataMutableSourceShippingClientStorageDataKey {
  LastSelectedSavedAddressId = "LastSelectedSavedAddressId",
  LastSelected_isBillingAndShippingTheSame = "LastSelected_isBillingAndShippingTheSame",
  LastSelected_authoriseDeliveryToBeLeftWithoutSignature = "LastSelected_authoriseDeliveryToBeLeftWithoutSignature",
}

export const DataMutableSourceShippingClientStorageDataProvider: React.FunctionComponent =
  (props) => {
    const parseStoredValue = React.useCallback((storedItem: unknown) => {
      if (storedItem === null || storedItem === undefined) {
        return undefined;
      }
      if (typeof storedItem === "string") {
        try {
          return JSON.parse(storedItem);
        } catch (anyError) {
          return storedItem;
        }
      }
      return storedItem;
    }, []);

    const [hasInitialized, setHasInitialized] = React.useState(false);

    const [lastSelectedSavedAddressId, setLastSelectedSavedAddressId] =
      React.useState(null);

    const [
      lastSelected_isBillingAndShippingTheSame,
      setLastSelected_isBillingAndShippingTheSame,
    ] = React.useState(null);

    const [
      lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
      setLastSelected_authoriseDeliveryToBeLeftWithoutSignature,
    ] = React.useState(null);

    const initialize = React.useCallback(async () => {
      const lastSelectedSavedAddress_storedItem = await LocalForage.getItem(
        DataMutableSourceShippingClientStorageDataKey.LastSelectedSavedAddressId
      );
      const lastSelectedSavedAddress_retrieved = parseStoredValue(
        lastSelectedSavedAddress_storedItem
      );
      if (lastSelectedSavedAddress_retrieved !== undefined) {
        setLastSelectedSavedAddressId(lastSelectedSavedAddress_retrieved);
      }

      const lastSelected_isBillingAndShippingTheSame_storedItem =
        await LocalForage.getItem(
          DataMutableSourceShippingClientStorageDataKey.LastSelected_isBillingAndShippingTheSame
        );
      const lastSelected_isBillingAndShippingTheSame_retrieved =
        parseStoredValue(lastSelected_isBillingAndShippingTheSame_storedItem);
      if (lastSelected_isBillingAndShippingTheSame_retrieved !== undefined) {
        setLastSelected_isBillingAndShippingTheSame(
          lastSelected_isBillingAndShippingTheSame_retrieved
        );
      }

      const lastSelected_authoriseDeliveryToBeLeftWithoutSignature_storedItem =
        await LocalForage.getItem(
          DataMutableSourceShippingClientStorageDataKey.LastSelected_authoriseDeliveryToBeLeftWithoutSignature
        );
      const lastSelected_authoriseDeliveryToBeLeftWithoutSignature_retrieved =
        parseStoredValue(
          lastSelected_authoriseDeliveryToBeLeftWithoutSignature_storedItem
        );
      if (
        lastSelected_authoriseDeliveryToBeLeftWithoutSignature_retrieved !==
        undefined
      ) {
        setLastSelected_authoriseDeliveryToBeLeftWithoutSignature(
          lastSelected_authoriseDeliveryToBeLeftWithoutSignature_retrieved
        );
      }

      setHasInitialized(true);
    }, [parseStoredValue]);
    React.useEffect(() => {
      initialize();
    }, []);

    const handleLastSelectedSavedAddressIdChange: DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelectedSavedAddressIdChange"] =
      React.useCallback(
        async (lastSelectedSavedAddressId) => {
          await LocalForage.setItem(
            DataMutableSourceShippingClientStorageDataKey.LastSelectedSavedAddressId,
            JSON.stringify(lastSelectedSavedAddressId)
          );
          setLastSelectedSavedAddressId(lastSelectedSavedAddressId);
        },
        [setLastSelectedSavedAddressId]
      );

    const handleLastSelected_isBillingAndShippingTheSameChange: DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelected_isBillingAndShippingTheSameChange"] =
      React.useCallback(
        async (lastSelected_isBillingAndShippingTheSame) => {
          await LocalForage.setItem(
            DataMutableSourceShippingClientStorageDataKey.LastSelected_isBillingAndShippingTheSame,
            JSON.stringify(lastSelected_isBillingAndShippingTheSame)
          );
          setLastSelected_isBillingAndShippingTheSame(
            lastSelected_isBillingAndShippingTheSame
          );
        },
        [setLastSelected_isBillingAndShippingTheSame]
      );

    const handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange: DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange"] =
      React.useCallback(
        async (lastSelected_authoriseDeliveryToBeLeftWithoutSignature) => {
          await LocalForage.setItem(
            DataMutableSourceShippingClientStorageDataKey.LastSelected_authoriseDeliveryToBeLeftWithoutSignature,
            JSON.stringify(lastSelected_authoriseDeliveryToBeLeftWithoutSignature)
          );
          setLastSelected_authoriseDeliveryToBeLeftWithoutSignature(
            lastSelected_authoriseDeliveryToBeLeftWithoutSignature
          );
        },
        [setLastSelected_authoriseDeliveryToBeLeftWithoutSignature]
      );

    const value: DataMutableSourceShippingClientStorageDataConsumerInterface =
      React.useMemo(
        () => ({
          hasInitialized,
          lastSelectedSavedAddressId,
          lastSelected_isBillingAndShippingTheSame,
          lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
        }),
        [
          hasInitialized,
          lastSelectedSavedAddressId,
          lastSelected_isBillingAndShippingTheSame,
          lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
        ]
      );
    const controllerValue: DataMutableSourceShippingClientStorageData_ControllerInterface =
      React.useMemo(
        () => ({
          handleLastSelectedSavedAddressIdChange,
          handleLastSelected_isBillingAndShippingTheSameChange,
          handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
        }),
        [
          handleLastSelectedSavedAddressIdChange,
          handleLastSelected_isBillingAndShippingTheSameChange,
          handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
        ]
      );
    return (
      <DataMutableSourceShippingClientStorageDataContext.Provider value={value}>
        <DataMutableSourceShippingClientStorageData_ControllerContext.Provider
          value={controllerValue}
        >
          <DataMutableSourceShippingClientStorageDataContext_44.Provider
            value={value}
          >
            {props?.children || []}
          </DataMutableSourceShippingClientStorageDataContext_44.Provider>
        </DataMutableSourceShippingClientStorageData_ControllerContext.Provider>
      </DataMutableSourceShippingClientStorageDataContext.Provider>
    );
  };

export const useDataMutableSourceShippingClientStorageData = () => {
  const context = React.useContext(
    DataMutableSourceShippingClientStorageDataContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutableSourceShippingClientStorageData must be used within a DataMutableSourceShippingClientStorageDataProvider"
    );
  }
  return context;
};

export const useDataMutableSourceShippingClientStorageData_asController =
  () => {
    const context = React.useContext(
      DataMutableSourceShippingClientStorageData_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutableSourceShippingClientStorageData_asController must be used within a DataMutableSourceShippingClientStorageDataProvider"
      );
    }
    return context;
  };

export const useDataMutableSourceShippingClientStorageData_44 = () => {
  const context = React44ms.React.useContext(
    DataMutableSourceShippingClientStorageDataContext_44
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutableSourceShippingClientStorageData_44 must be used within a DataMutableSourceShippingClientStorageDataProvider"
    );
  }
  return context;
};
